<template>

  <div>
      <!-- XOL-34 / XOL-60: Telco users must not see "Add Site". This is the single UI choke point; -->
      <!-- the backend enforces the same rule (XOL-61). Do not remove without updating both sides. -->
      <xolas-table
        :title="title ? title : (menu === 'all' ? 'All Sites' : titleCase(menu, '-') + ' Sites')"
        icon="fa fa-broadcast-tower"
        ref="sitetable"
        table-ref="sitetable"
        :url="url"
        :fields="siteFields"
        sort-field="siteName"
        :show-refresh="true"
        :params="props"
        search-placeholder="Site IDs, Site Names, Address..."
        :layer-component="disableLayer ? null : 'site'"
        :layer-args="{ id: 'id' }">

        <template #below-filter>
          <site-menu
            :view="menu"
            :show="showStatusLocal"
            :stats="addProps.stats" />
        </template>

        <template #actions>
          <!-- XOL-34/XOL-60: Telco gate applied on site-action — Add Site only renders for TowerCo managers.
               @update-stats: SiteAction emits this on save/delete; relay to the global refreshStats mixin
               so dashboard / header counters stay fresh after Add Site. Previously caught on the root <div>
               in the legacy template; rewiring directly on the emitter is the reliable path (custom events
               don't bubble through DOM boundaries). -->
          <site-action
            v-if="isTowerCo() && isManager() && !isViewOnly()"
            :add-site="true"
            :status="addProps.status"
            @update-stats="refreshStats" />
        </template>

        <!-- XolasTable forwards #empty into el-table's empty slot.
             Element UI 2.x does NOT ship <el-empty> (that's Element Plus / Vue 3),
             so we hand-roll the empty state with the Font Awesome inbox icon
             already used elsewhere in the SPA. -->
        <template #empty>
          <div class="sites-empty">
            <i class="fa fa-inbox sites-empty__icon" />
            <p class="sites-empty__text">No sites found</p>
          </div>
        </template>

        <template #site="props">

          <div
            class="sites-row sites-row--compact"
            :style="{ borderLeftColor: siteBgColor(props.rowData.status) }">

            <!-- Left region: identity + footer dates -->
            <div class="sites-row__left">
              <div class="sites-row__identity">
                <a
                  :class="['sites-row__name', 'xolas-table__row-link', titleClass(props.rowData.status)]"
                  :href="layerHref(props.rowData)"
                  :aria-label="'Open detail for ' + props.rowData.siteName"
                  @click.prevent="openRow(props.rowData, $event)"
                  @mousedown="openRowMouseDown(props.rowData, $event)">{{props.rowData.siteName}}</a>
                <span class="sites-row__code">
                  {{props.rowData.siteId}}<b v-if="props.rowData.siteGroup">&nbsp;{{props.rowData.siteGroup}}</b>
                  <span v-if="props.rowData.telcoSiteId" class="siteid-bubble">
                    <b>{{props.rowData.telcoSiteId}}</b>
                  </span>
                </span>
                <!-- Status badge: icon + visible text label, sits below the
                     site ID. siteStatus(value, false) emits both the icon and
                     the readable status (formatSiteStatus uses shorten=true
                     which hides the label behind a hover tooltip — too subtle
                     for the row scan). -->
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span v-if="isTowerCo()" class="sites-row__status" v-html="siteStatus(props.rowData.status, false)" />
                <span v-if="props.rowData.address" class="sites-row__address">{{props.rowData.address}}</span>
                <org-badge v-if="isCandidate(props.rowData.status)" :organisation="props.rowData.candidate" />
                <div v-if="isTelco() && props.rowData.status === 'Active'" class="sites-row__rtu-rtb">
                  <request-action
                    v-if="!props.rowData.stats || !props.rowData.stats.rtu || props.rowData.stats.rtu === 0"
                    button-size="mini"
                    button-type="round"
                    type="RTU"
                    :site-id="props.rowData.id"
                    :row-data="props.rowData" />
                  <request-action
                    v-if="!props.rowData.stats || !props.rowData.stats.rtb || props.rowData.stats.rtb === 0"
                    button-size="mini"
                    button-type="round"
                    type="RTB"
                    :site-id="props.rowData.id"
                    :row-data="props.rowData" />
                </div>
              </div>
              <div class="sites-row__left-footer">
                <div v-if="isTowerCo() && (props.rowData.dismantled || props.rowData.handover)">
                  <span v-if="props.rowData.dismantled && props.rowData.status === 'Dismantled'">
                    <span class="text-danger">Dismantled&nbsp;<b>{{formatDate(props.rowData.dismantled)}}</b></span>
                  </span>
                  <span v-else>
                    <span class="text-info">Handover&nbsp;<b>{{formatDate(props.rowData.handover)}}</b></span>
                  </span>
                </div>
                <div v-else>
                  <span class="text-black"><span v-if="props.rowData.onAir">On Air</span>&nbsp;<b>{{formatDate(props.rowData.onAir)}}</b></span>
                </div>
              </div>
            </div>

            <!-- Stats region: card-strip layout -->
            <div class="sites-row__stats">
              <site-stats
                v-if="isStats(props.rowData)"
                :site-id="props.rowData.id"
                :stats="props.rowData.stats"
                :show-shadow="false" />
            </div>

            <!-- Right region: map + footer actions -->
            <div class="sites-row__right">
              <div class="sites-row__map">
                <site-card :site-data="props.rowData" use-leaflet hide-site height="200px" />
              </div>
              <div class="sites-row__right-footer">
                <el-tooltip :content="formatDateTime(props.rowData.updated_at) + (props.rowData.updated_by ? ' by ' + props.rowData.updated_by : '')">
                  <span>Last Update <strong>{{formatFriendlyDate(props.rowData.updated_at)}}</strong></span>
                </el-tooltip>
                <el-button
                  v-if="canDeleteSite(props.rowData) && isDesktop"
                  type="danger"
                  plain
                  circle
                  size="mini"
                  icon="fa fa-trash"
                  :aria-label="'Delete site ' + props.rowData.siteName"
                  @click="deleteSite(props.rowData)" />
              </div>
            </div>

          </div>

        </template>

      </xolas-table>

    </div>

</template>

<script>

import SiteMixins from '@/mixins/SiteMixins'

import FormattingMixins from '@/mixins/FormattingMixins'

import {MAP_SEARCH_EVENT, MAP_RESET_EVENT, SITETABLE_VIEW_EVENT} from '@/components/Data'

import SiteAction from '@/components/actions/SiteAction'
import SiteCard from '@/components/sites/SiteCard'
import SiteStats from '@/components/sites/SiteStats'
import SiteMenu from '@/components/tables/details/SiteMenu'

import RequestAction from '@/components/actions/RequestAction'
import { pushDrawer, resolveDrawerHref } from '@/lib/layerNav'

export default {
  mixins: [FormattingMixins, SiteMixins],

  components: {
    SiteAction,
    SiteCard,
    SiteStats,
    SiteMenu,
    RequestAction
  },

  props: {

    title: {
      type: String,
      default: null
    },

    view: {
      type: String,
      default: 'all'
    },

    sharer: {
      type: Number,
      default: null
    },

    params: {
      type: Object,
      default: null
    },

    geoSearch: {
      type: Object,
      default: null
    },

    condensed: {
      type: Boolean,
      default: false
    },

    showStatus: {
      type: Boolean,
      default: true
    },

    showMap: {
      type: Boolean,
      default: true
    },

    showDetail: {
      type: Boolean,
      default: false
    },

    disableLayer: {
      type: Boolean,
      default: false
    }
  },



  methods: {


    selectSite (index) {
      this.menu = index
      this.addProps.status = this.titleCase(index)
    },



    isStats (row) {
      let st = row.stats
      // Scoped non-sharer orgs (e.g. Contractor) get stats: null from /api/sites
      // by design — per-site stats are sharer-derived (XOL-292/XOL-295). Guard
      // the deref so a null stats renders an empty stats cell, not a crashed table.
      if (!st) return false
      return (st.tickets || st.permits || st.assets || st.actions || st.requests || st.sharers || st.landlords || st.rts || st.rtu || st.rtb || st.backhaul)
    },



    canDeleteSite (row) {

      const canDelete = (this.isTowerCo() && this.isCandidate(row.status) && this.isManager('finance|management|project')) || this.isSuper()

      return canDelete && !this.isViewOnly()
    },



    getStructName (row) {
      if (row.structure && row.structure.name) {
        return row.structure.name
      } else {
        if (row.structureType) {
          return row.structureType
        }
      }

      return null
    },



    getStructHeight (row) {
      if (row.structure && row.structure.height) {
        return row.structure.height
      } else {
        if (row.structureHeight) {
          return row.structureHeight
        }
      }

      return null
    },



    mapFilter (search) {
      // State mutations dirty `url` → watch.url owns the (deferred) refresh.
      // Don't refresh here directly — it would refetch with the stale prop.
      this.isMapActive = true
      this.showStatusLocal = false
      this.mapSearch = search
    },



    mapClear () {
      // Same as mapFilter — watch.url handles the refresh on next tick.
      this.isMapActive = false
      this.showStatusLocal = true
      this.mapSearch = null
    },



    deleteSite (rowData) {
      this.deleteItem(this, rowData.id, '/sites', rowData.siteId + ' ' + rowData.siteName + ' AND all it\'s data, including sharers. Are you <i>absolutely</i> sure?', this.onDelete)
    },

    onDelete (response) {
      this.$events.emit('sitetable-refresh-table')
      this.$emit('update-stats')
    },

    layerHref (rowData) {
      if (this.disableLayer || !rowData || !rowData.id) return '#'
      return resolveDrawerHref(this.$router, { open: { site: { id: rowData.id } } })
    },

    openRow (rowData, event) {
      if (this.disableLayer) return
      pushDrawer(this.$router, { open: { site: { id: rowData.id } } })
    },

    openRowMouseDown (rowData, event) {
      if (this.disableLayer) return
      const isNewTab = event.metaKey || event.ctrlKey || event.button === 1
      if (isNewTab) {
        event.preventDefault()
        window.open(this.layerHref(rowData), '_blank')
      }
    }

  },



  computed: {
    url: function () {
      if (this.isMapActive && this.mapSearch) {
        return '/sites/map/' + (this.sharerId ? 'org/' + this.sharerId + '/' : '') +
          this.mapSearch.lat + '/' + this.mapSearch.lng + '/' + this.mapSearch.radius + '/km/10/'
      } else {
        return (this.menu !== 'all' ? '/sites/' + this.menu : '/sites') + (this.sharerId
            ? '/sharer/' + Number(this.sharerId)
          : '')
      }
    }
  },




  watch: {

    url: function () {
      // $nextTick: the watcher fires synchronously with the `url` recompute,
      // but Vue propagates the new `:url` prop to <xolas-table> on the next
      // render tick. Calling refresh() in the same tick re-fetches with the
      // STALE prop value (user clicks "Exclusive" → table still shows previous
      // filter's results). Defer one tick so XolasTable sees the new url
      // before the refetch fires.
      this.$nextTick(() => {
        this.$refs.sitetable && this.$refs.sitetable.refresh()
      })
    },

    geoSearch: function (value) {
      this.mapSearch = value
      this.isMapActive = value ? true : false
    },

    sharer: function (value) {
      // Bumping sharerId recomputes `url`; the `url` watcher above owns the
      // refresh call — don't double-fire.
      this.sharerId = value
    },

    stats: function (newValue) {
      this.stats = newValue
    }
  },




  data () {

    return {

      menu: this.view.toLowerCase(),
      showStatusLocal: this.showStatus,

      addProps: {
        status: this.view === 'all' ? null : this.view,
        view: this.view,
        stats: null,
        show: this.showStatus,
        addSite: true
      },

      props: this.params ? this.params : {sharer: this.sharerId},

      sharerId: this.isTelco() ? this.org().id : this.sharer,
      isMapActive: false,
      mapSearch: this.geoSearch,

      stats: this.getStats() ? this.getStats() : {},

      sortFields: ['siteName', 'siteId', 'handover', 'zone', 'subdistrict', 'district', 'state', 'region'],

      siteFields: [
        {
          name: '__slot:site'
        }
      ]
    }

  },


  created () {

    this.addProps.stats = this.stats

    if (this.isTelco()) {
      this.sortFields.push('onAir')
      this.sortFields.push('telcoSiteId')

      if (!this.sharerId) {
        this.sharerId = Number(this.org().id)
      }

    }

    this.$events.on(MAP_SEARCH_EVENT, this.mapFilter)
    this.$events.on(MAP_RESET_EVENT, this.mapClear)
    this.$events.on(SITETABLE_VIEW_EVENT, this.selectSite)

  },



  beforeDestroy () {
    this.$events.off(MAP_SEARCH_EVENT, this.mapFilter)
    this.$events.off(MAP_RESET_EVENT, this.mapClear)
    this.$events.off(SITETABLE_VIEW_EVENT, this.selectSite)
  }

}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity .5s;
}

.slide-fade-enter-active {
  transition: all .3s ease;
}
.slide-fade-leave-active {
  transition: all .8s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}

/* ── Compact row ──────────────────────────────────────────────────────── */
.sites-row--compact {
  display: flex;
  align-items: stretch;
  gap: 16px;
  padding: 12px 16px;
  border-left: 8px solid transparent;
  border-bottom: 1px solid #ebeef5;
  background: transparent;
}

.sites-row--compact:hover {
  background: #F5F7FA;
}

.sites-row--compact:hover .sites-row__name {
  text-decoration: underline;
}

/* Left region: identity + footer dates */
.sites-row__left {
  flex: 0 0 280px;
  display: flex;
  flex-direction: column;
}

.sites-row__left-footer {
  margin-top: auto;
  font-size: 12px;
  color: #909399;
}

.sites-row__identity {
  display: flex;
  flex-direction: column;
}

.sites-row__name {
  display: block;
  font-size: 18px;
  font-weight: 500;
  text-decoration: none;
}

.sites-row__code {
  display: block;
  font-size: 13px;
  color: #606266;
}

.sites-row__status {
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  margin: 2px 0 4px;
}
.sites-row__status ::v-deep i.fa {
  margin-right: 6px;
}
/* The tooltip wrapper formatSiteStatus() emits is inline by default —
   keep the status badge on its own row under the site name. */
.sites-row__status ::v-deep .tooltip {
  display: inline-flex;
  align-items: center;
}

.sites-row__address {
  display: block;
  margin-top: 8px;
  font-size: 13px;
  color: #606266;
  line-height: 1.35;
}

.sites-row__rtu-rtb {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

/* Stats region: card-strip layout */
.sites-row__stats {
  flex: 1 1 auto;
  display: flex;
  align-items: flex-start;
  overflow: hidden;
}

/* Tighten SiteStats card-mode rendering when it lives inside a Compact row.
   Scoped to `.sites-row__stats` so the dashboard / drawer call sites stay
   on the legacy padding.

   Width consistency: force the SiteStats el-row to a flex strip and pin
   each card to a fixed 110px width. el-row's negative margins + el-col's
   gutter padding fight us otherwise (single-card rows collapsed to 26px). */
.sites-row__stats {
  min-height: 150px;          /* keep the row block height stable across sites */
  align-items: center;        /* vertically center the stats strip in its band */
}
/* SiteStats's template root is `<el-row>` (the card strip). Direct-child
   combinator skips matching DashText's nested el-rows / el-cols inside
   each card, so the per-stat caption row keeps its natural full-width
   center alignment instead of being forced to 110px. */
.sites-row__stats ::v-deep > .el-row {
  display: flex !important;
  align-items: stretch;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 !important;       /* kill el-row gutter negative margins */
  width: 100%;
}
.sites-row__stats ::v-deep > .el-row > .el-col {
  flex: 0 0 110px !important;
  max-width: 110px !important;
  padding: 0 !important;      /* kill el-col gutter padding */
  display: flex;
  align-items: stretch;
}
.sites-row__stats ::v-deep .el-card {
  width: 100%;
  min-height: 130px;
  display: flex;
  flex-direction: column;
  border-radius: 4px;
}
.sites-row__stats ::v-deep .el-card__header {
  padding: 6px 8px;
  font-size: 12px;
  line-height: 1.2;
  text-align: center;
  font-weight: 500;
}
.sites-row__stats ::v-deep .el-card__body {
  flex: 1;
  padding: 6px 6px 8px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}
.sites-row__stats ::v-deep .text-dash,
.sites-row__stats ::v-deep .text-click {
  padding: 8px 0 4px;        /* extra top padding gives a clear gap between sibling segments */
  text-align: center;
  line-height: 1;
}
.sites-row__stats ::v-deep .text-dash:first-of-type,
.sites-row__stats ::v-deep .text-click:first-of-type {
  padding-top: 2px;          /* first segment hugs the card header */
}

/* Stack each DashText item as count → icon → label (3 lines, tightly
   packed and centered). DashText renders count + icon inline in the same
   el-col with a `&nbsp;` separator. Force the el-col to flex-column and
   zero the text-node font size to swallow the &nbsp; — the span/icon are
   explicit elements and get their own line each. The default DashText
   layout uses 2em font sizes + el-row inherited line-height that pushes
   the rows apart; we collapse line-height to 1 throughout so the three
   lines read as a single unit. */
.sites-row__stats ::v-deep .text-dash > .el-row:first-child,
.sites-row__stats ::v-deep .text-click > .el-row:first-child {
  line-height: 1;
}
.sites-row__stats ::v-deep .text-dash > .el-row:first-child .el-col,
.sites-row__stats ::v-deep .text-click > .el-row:first-child .el-col {
  display: flex !important;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  font-size: 0;
  flex: none !important;
  max-width: none !important;
  padding: 0 !important;
  line-height: 1;
}
.sites-row__stats ::v-deep .text-dash > .el-row:first-child .el-col > span,
.sites-row__stats ::v-deep .text-click > .el-row:first-child .el-col > span {
  font-size: 16px !important;
  line-height: 1;
  text-align: center;
  display: block;
}
.sites-row__stats ::v-deep .text-dash > .el-row:first-child .el-col > i.fa,
.sites-row__stats ::v-deep .text-click > .el-row:first-child .el-col > i.fa {
  font-size: 16px !important;
  line-height: 1;
  display: block;
}
/* Caption (row 3 — text label) — tight margin so it tucks under the icon */
.sites-row__stats ::v-deep .text-dash > .el-row:nth-child(2),
.sites-row__stats ::v-deep .text-click > .el-row:nth-child(2) {
  font-size: 11px !important;
  line-height: 1.1;
  margin-top: 1px;
  text-align: center;
}

/* Right region: map + footer actions */
.sites-row__right {
  flex: 0 0 320px;
  display: flex;
  flex-direction: column;
}

.sites-row__map {
  height: auto;                /* let inner SiteCard drive, plus padding */
  margin-bottom: 8px;
  padding-bottom: 12px;        /* exposes the box's tint below the map tile */
  background-color: #F5F7FA;   /* visible band under the map */
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  overflow: hidden;
  transition: box-shadow 0.15s;
}
/* SiteCard's el-card defaults to a tall inner height (~265px); lock it
   to the visible 200px so the padding-bottom above isn't swallowed. */
.sites-row__map ::v-deep > .el-card {
  height: 200px;
  border: none;
  background: transparent;
}

.sites-row__map:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.sites-row__right-footer {
  margin-top: auto;
  padding-top: 4px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  font-size: 12px;
  color: #909399;
}

/* Element UI sets `word-break: break-all` on `.el-table .cell`, which cascades
   into our row content and forces per-character vertical wrap
   ("A-s-s-e-t-s & S-e-r-v-i-c-e-s", "Last Update 2 y / ears ago"). Production
   didn't hit this because vuetable-2 used raw HTML rows, not el-table cells.
   Restore normal word-wrap inside both row modes: Compact root .sites-row
   AND any el-card nested in a table cell (Classic badge headers/bodies). */
::v-deep .el-table .cell .sites-row,
::v-deep .el-table .cell .el-card__header,
::v-deep .el-table .cell .el-card__body {
  word-break: normal;
}

/* Empty state — Element UI 2.x has no <el-empty>; hand-rolled. */
.sites-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 16px;
  color: #909399;
}
.sites-empty__icon {
  font-size: 48px;
  color: #DCDFE6;
}
.sites-empty__text {
  margin: 12px 0 0;
  font-size: 14px;
}
</style>
